import {useState} from "react";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {Accordion} from "@pg-design/accordion";
import {CenteredImage} from "@pg-design/centered-image";
import {elevation} from "@pg-design/elevation";
import {borderRadius, calculateRemSize as crs, flex, flexDirection, p} from "@pg-design/helpers-css";
import {BrandMedalIcon, BrandRankingIcon} from "@pg-design/icons-module";
import {Rating} from "@pg-design/rating";
import {Text} from "@pg-design/text-module";

import {useResponsiveLinkTarget} from "../../../../hooks/use_responsive_link_target";
import {useOfferLink} from "../../../../offer/detail/hooks/use_offer_link";
import {IOfferDetail} from "../../../../offer/types/IOfferDetail";
import {rankingBigViewBreakpoint} from "../../../constants/ranking_big_view_breakpoint";
import {RankingOfferData} from "../../RankingOfferData";

interface IProps {
    place: number;
    score: number;
    ratingDescription: string;
    offer: Pick<IOfferDetail, "name" | "main_image" | "street_name" | "street_number" | "region" | "vendor" | "slug" | "id" | "groups">;
}

export function OfferRankingCard(props: IProps) {
    const {place, score, ratingDescription, offer} = props;
    const {main_image, name} = offer;

    const [accordionCollapsed, setAccordionCollapsed] = useState(false);

    const isFirst = place === 1;

    const offerLink = useOfferLink(offer);
    const target = useResponsiveLinkTarget();

    return (
        <RankingCardWrap isFirst={isFirst}>
            <RankingCard isFirst={isFirst} accordionCollapsed={accordionCollapsed}>
                <span css={cardTitle}>
                    {isFirst ? (
                        <BrandRankingIcon wrapperSize="4.8" wrapperColor="transparent" size="4.8" />
                    ) : (
                        <BrandMedalIcon wrapperSize="4.8" wrapperColor="transparent" size="4.8" />
                    )}
                    <Text as="p" variant="headline_6">
                        {place} miejsce
                    </Text>
                </span>
                <a href={offerLink} target={target}>
                    {isFirst ? (
                        <CenteredImage
                            width={360}
                            height={360}
                            maxSourceWidth={385}
                            breakpoints={[{mediaWidth: "0", width: "auto", height: "360px"}]}
                            alt={name}
                            src={main_image.m_img_440x440}
                        />
                    ) : (
                        <CenteredImage
                            width={360}
                            height={280}
                            maxSourceWidth={360}
                            breakpoints={[{mediaWidth: "0", width: "auto", height: "280px"}]}
                            alt={name}
                            src={main_image.m_img_440x440}
                        />
                    )}
                </a>
                <CardContent accordionCollapsed={accordionCollapsed}>
                    <RankingOfferData offer={offer} />
                    <div css={flex()}>
                        <div css={bottomRow}>
                            <Accordion
                                onChange={(collapsed) => {
                                    setAccordionCollapsed(collapsed);
                                }}
                                css={[flex("center"), flexDirection("column")]}
                            >
                                <div css={bottomRowItemsWrap}>
                                    <Rating css={rating} score={score} isReadOnly showScore />
                                    <Accordion.Header css={accordionToggle}>{accordionCollapsed ? "Rozwiń" : "Zwiń"}</Accordion.Header>
                                </div>
                                <Accordion.Content wrapCss={accordionWrap(isFirst)}>{ratingDescription}</Accordion.Content>
                            </Accordion>
                        </div>
                    </div>
                </CardContent>
            </RankingCard>
        </RankingCardWrap>
    );
}

// Styles
const RankingCardWrap = styled.li<{isFirst: boolean}>`
    ${({isFirst}) => css`
        position: relative;
        ${isFirst
            ? css`
                  order: 0;
              `
            : css`
                  order: 1;
              `};

        @media screen and (min-width: ${rankingBigViewBreakpoint}) {
            order: initial;
            ${isFirst
                ? css`
                      grid-row-start: 1;
                      grid-row-end: span 3;
                  `
                : css`
                      height: 100%;
                      grid-row-start: 2;
                      grid-row-end: 2;
                  `}
        }
    `}
`;

const RankingCard = styled.div<{isFirst: boolean; accordionCollapsed: boolean}>`
    ${({isFirst, accordionCollapsed}) => css`
        display: flex;
        flex-direction: column;
        position: relative;
        max-width: 360px;
        background: white;
        overflow: visible;
        ${cardElevation(isFirst)};
        ${borderRadius(2)};

        @media screen and (min-width: ${rankingBigViewBreakpoint}) {
            height: 100%;
            ${accordionCollapsed ? borderRadius(2) : borderRadius(2, 2, 0, 0)}
        }
    `}
`;

const cardTitle = (theme: Theme) => css`
    display: flex;
    align-items: center;
    gap: ${crs(1)};
    ${p(1.5)};
    justify-content: center;
    background: ${theme.colors.primary};
    ${borderRadius(2, 2, 0, 0)}
`;

const bottomRow = css`
    flex: 1;
`;

const bottomRowItemsWrap = css`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const rating = css`
    flex: 1 0 auto;
`;

const accordionToggle = css`
    flex: 0 0 auto;
    ${p(1, 0.5, 1, 2)};
    min-height: auto;
    width: auto;
    text-transform: uppercase;
    font-weight: 600;
    display: flex;
    gap: ${crs(1.5)};
`;

const CardContent = styled.div<{accordionCollapsed: boolean}>`
    ${({accordionCollapsed}) => css`
        ${p(2)};
        gap: ${crs(2)};
        display: flex;
        flex-direction: column;
        flex: 1;
        ${borderRadius(0, 0, 2, 2)};

        @media screen and (min-width: ${rankingBigViewBreakpoint}) {
            ${accordionCollapsed ? borderRadius(0, 0, 2, 2) : borderRadius(0)};
        }
    `}
`;

//  We're braking "styled component when props" rule here, because default className is attached to inner component in Accordion
const accordionWrap = (isFirst: boolean) => css`
    ${borderRadius(0, 0, 2, 2)};
    @media screen and (min-width: ${rankingBigViewBreakpoint}) {
        ${cardElevation(isFirst)};
        clip-path: inset(0 -60px -60px -60px);
        position: absolute;
        z-index: 2;
        top: 99%;
        left: 0;
    }
`;

const cardElevation = (isFirst: boolean) => (isFirst ? elevation(4) : elevation(2));
import {useState} from "react";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {Accordion} from "@pg-design/accordion";
import {CenteredImage} from "@pg-design/centered-image";
import {elevation} from "@pg-design/elevation";
import {borderRadius, calculateRemSize as crs, flex, flexDirection, p} from "@pg-design/helpers-css";
import {BrandMedalIcon, BrandRankingIcon} from "@pg-design/icons-module";
import {Rating} from "@pg-design/rating";
import {Text} from "@pg-design/text-module";

import {useResponsiveLinkTarget} from "../../../../hooks/use_responsive_link_target";
import {useOfferLink} from "../../../../offer/detail/hooks/use_offer_link";
import {IOfferDetail} from "../../../../offer/types/IOfferDetail";
import {rankingBigViewBreakpoint} from "../../../constants/ranking_big_view_breakpoint";
import {RankingOfferData} from "../../RankingOfferData";

interface IProps {
    place: number;
    score: number;
    ratingDescription: string;
    offer: Pick<IOfferDetail, "name" | "main_image" | "street_name" | "street_number" | "region" | "vendor" | "slug" | "id" | "groups">;
}

export function OfferRankingCard(props: IProps) {
    const {place, score, ratingDescription, offer} = props;
    const {main_image, name} = offer;

    const [accordionCollapsed, setAccordionCollapsed] = useState(false);

    const isFirst = place === 1;

    const offerLink = useOfferLink(offer);
    const target = useResponsiveLinkTarget();

    return (
        <RankingCardWrap isFirst={isFirst}>
            <RankingCard isFirst={isFirst} accordionCollapsed={accordionCollapsed}>
                <span css={cardTitle}>
                    {isFirst ? (
                        <BrandRankingIcon wrapperSize="4.8" wrapperColor="transparent" size="4.8" />
                    ) : (
                        <BrandMedalIcon wrapperSize="4.8" wrapperColor="transparent" size="4.8" />
                    )}
                    <Text as="p" variant="headline_6">
                        {place} miejsce
                    </Text>
                </span>
                <a href={offerLink} target={target}>
                    {isFirst ? (
                        <CenteredImage
                            width={360}
                            height={360}
                            maxSourceWidth={385}
                            breakpoints={[{mediaWidth: "0", width: "auto", height: "360px"}]}
                            alt={name}
                            src={main_image.m_img_440x440}
                        />
                    ) : (
                        <CenteredImage
                            width={360}
                            height={280}
                            maxSourceWidth={360}
                            breakpoints={[{mediaWidth: "0", width: "auto", height: "280px"}]}
                            alt={name}
                            src={main_image.m_img_440x440}
                        />
                    )}
                </a>
                <CardContent accordionCollapsed={accordionCollapsed}>
                    <RankingOfferData offer={offer} />
                    <div css={flex()}>
                        <div css={bottomRow}>
                            <Accordion
                                onChange={(collapsed) => {
                                    setAccordionCollapsed(collapsed);
                                }}
                                css={[flex("center"), flexDirection("column")]}
                            >
                                <div css={bottomRowItemsWrap}>
                                    <Rating css={rating} score={score} isReadOnly showScore />
                                    <Accordion.Header css={accordionToggle}>{accordionCollapsed ? "Rozwiń" : "Zwiń"}</Accordion.Header>
                                </div>
                                <Accordion.Content wrapCss={accordionWrap(isFirst)}>{ratingDescription}</Accordion.Content>
                            </Accordion>
                        </div>
                    </div>
                </CardContent>
            </RankingCard>
        </RankingCardWrap>
    );
}

// Styles
const RankingCardWrap = styled.li<{isFirst: boolean}>`
    ${({isFirst}) => css`
        position: relative;
        ${isFirst
            ? css`
                  order: 0;
              `
            : css`
                  order: 1;
              `};

        @media screen and (min-width: ${rankingBigViewBreakpoint}) {
            order: initial;
            ${isFirst
                ? css`
                      grid-row-start: 1;
                      grid-row-end: span 3;
                  `
                : css`
                      height: 100%;
                      grid-row-start: 2;
                      grid-row-end: 2;
                  `}
        }
    `}
`;

const RankingCard = styled.div<{isFirst: boolean; accordionCollapsed: boolean}>`
    ${({isFirst, accordionCollapsed}) => css`
        display: flex;
        flex-direction: column;
        position: relative;
        max-width: 360px;
        background: white;
        overflow: visible;
        ${cardElevation(isFirst)};
        ${borderRadius(2)};

        @media screen and (min-width: ${rankingBigViewBreakpoint}) {
            height: 100%;
            ${accordionCollapsed ? borderRadius(2) : borderRadius(2, 2, 0, 0)}
        }
    `}
`;

const cardTitle = (theme: Theme) => css`
    display: flex;
    align-items: center;
    gap: ${crs(1)};
    ${p(1.5)};
    justify-content: center;
    background: ${theme.colors.primary};
    ${borderRadius(2, 2, 0, 0)}
`;

const bottomRow = css`
    flex: 1;
`;

const bottomRowItemsWrap = css`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const rating = css`
    flex: 1 0 auto;
`;

const accordionToggle = css`
    flex: 0 0 auto;
    ${p(1, 0.5, 1, 2)};
    min-height: auto;
    width: auto;
    text-transform: uppercase;
    font-weight: 600;
    display: flex;
    gap: ${crs(1.5)};
`;

const CardContent = styled.div<{accordionCollapsed: boolean}>`
    ${({accordionCollapsed}) => css`
        ${p(2)};
        gap: ${crs(2)};
        display: flex;
        flex-direction: column;
        flex: 1;
        ${borderRadius(0, 0, 2, 2)};

        @media screen and (min-width: ${rankingBigViewBreakpoint}) {
            ${accordionCollapsed ? borderRadius(0, 0, 2, 2) : borderRadius(0)};
        }
    `}
`;

//  We're braking "styled component when props" rule here, because default className is attached to inner component in Accordion
const accordionWrap = (isFirst: boolean) => css`
    ${borderRadius(0, 0, 2, 2)};
    @media screen and (min-width: ${rankingBigViewBreakpoint}) {
        ${cardElevation(isFirst)};
        clip-path: inset(0 -60px -60px -60px);
        position: absolute;
        z-index: 2;
        top: 99%;
        left: 0;
    }
`;

const cardElevation = (isFirst: boolean) => (isFirst ? elevation(4) : elevation(2));
